import React, { useState, useEffect } from 'react';
import { Lock, LogOut, LogIn, AlertCircle, CheckCircle2, XCircle, Loader, Play, Square } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useDevice } from '../context/DeviceContext';
import { useTenant } from '../context/TenantContext';
import { MedicalKit, Role, Shift, Vehicle } from '../types';
import { usersCollectionRef } from '../services/firestorePaths';
import {
  crewSignOn,
  crewSignOff,
  CrewSignOnMetadata,
  getActiveCrewShift,
  checkWeeklyComplianceHours,
  startBreak,
  endBreak,
  evaluateShiftStartReadiness,
  evaluateShiftSignOffReconciliation,
  ShiftSignOffDiscrepancy,
  ShiftSignOffReconciliationInput,
  ShiftReadinessInput,
} from '../services/crewService';
import { ensureCrewDispatchUnit } from '../services/dispatchService';

interface CrewSignOnProps {
  onSignOnComplete?: (shiftId: string) => void;
  onSignOffComplete?: () => void;
  tenantId: string;
}

const buildGhostCallSign = (name: string | undefined, uid: string | undefined): string => {
  const safeName = (name || 'crew').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const nameToken = safeName.slice(0, 4).padEnd(4, 'X');
  const uidToken = (uid || '0000').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(-4).padStart(4, '0');
  return `GHOST-${nameToken}-${uidToken}`;
};

const CrewSignOnComponent: React.FC<CrewSignOnProps> = ({
  onSignOnComplete,
  onSignOffComplete,
  tenantId,
}) => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const { isRegistered: isDeviceRegistered, isChecking: isCheckingDevice } = useDevice();
  const [mode, setMode] = useState<'selector' | 'signOn' | 'signOff' | 'active'>('selector');
  const [pinError, setPinError] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [vehicleCallSign, setVehicleCallSign] = useState('');
  const [ghostModeEnabled, setGhostModeEnabled] = useState(false);
  const [ghostCallSign, setGhostCallSign] = useState('');
  const [shiftStartTime, setShiftStartTime] = useState('');
  const [shiftEndTime, setShiftEndTime] = useState('');
  const [odometerStart, setOdometerStart] = useState('');
  const [vdiCompleted, setVdiCompleted] = useState(false);
  const [vdiNotes, setVdiNotes] = useState('');
  const [crewSearch, setCrewSearch] = useState('');
  const [employeeIdSearch, setEmployeeIdSearch] = useState('');
  const [availableCrew, setAvailableCrew] = useState<Array<{ uid: string; name: string; role: Role; employeeId?: string }>>([]);
  const [selectedAdditionalCrew, setSelectedAdditionalCrew] = useState<Array<{ uid: string; name: string; role: Role; employeeId?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [breakLoading, setBreakLoading] = useState(false);
  const [activeShift, setActiveShift] = useState<any>(null);
  const [weeklyHours, setWeeklyHours] = useState<number | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessInput, setReadinessInput] = useState<ShiftReadinessInput | null>(null);
  const [readinessMessage, setReadinessMessage] = useState('');
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideApprover, setOverrideApprover] = useState('');
  const [signOffReason, setSignOffReason] = useState('');
  const [controlledDrugCheckCompleted, setControlledDrugCheckCompleted] = useState(false);
  const [assetCheckCompleted, setAssetCheckCompleted] = useState(false);
  const [signOffDiscrepancies, setSignOffDiscrepancies] = useState<ShiftSignOffDiscrepancy[]>([]);
  const [newDiscrepancyType, setNewDiscrepancyType] = useState<'ControlledDrug' | 'Asset'>('ControlledDrug');
  const [newDiscrepancySeverity, setNewDiscrepancySeverity] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [newDiscrepancyDescription, setNewDiscrepancyDescription] = useState('');
  const [newDiscrepancyReference, setNewDiscrepancyReference] = useState('');
  const [signOffOverrideEnabled, setSignOffOverrideEnabled] = useState(false);
  const [signOffOverrideReason, setSignOffOverrideReason] = useState('');
  const [signOffOverrideApprover, setSignOffOverrideApprover] = useState('');

  useEffect(() => {
    checkActiveShift();
  }, []);

  useEffect(() => {
    if (mode !== 'active') {
      return;
    }

    setSignOffReason('');
    setControlledDrugCheckCompleted(false);
    setAssetCheckCompleted(false);
    setSignOffDiscrepancies([]);
    setNewDiscrepancyType('ControlledDrug');
    setNewDiscrepancySeverity('Medium');
    setNewDiscrepancyDescription('');
    setNewDiscrepancyReference('');
    setSignOffOverrideEnabled(false);
    setSignOffOverrideReason('');
    setSignOffOverrideApprover('');
    setPinError('');
  }, [activeShift?.id, mode]);

  useEffect(() => {
    if (mode !== 'signOn') {
      return;
    }
    setGhostCallSign((previous) => previous || buildGhostCallSign(user?.name, user?.uid));
  }, [mode, user?.name, user?.uid]);

  useEffect(() => {
    if (mode !== 'signOn') {
      return;
    }

    let cancelled = false;
    const loadVehicles = async () => {
      try {
        const vehiclesSnap = await getDocs(
          query(collection(db, 'fleet'), where('tenantId', '==', tenantId))
        );

        if (cancelled) return;

        const loadedVehicles = vehiclesSnap.docs.map((docSnap) => ({
          ...(docSnap.data() as Vehicle),
          id: docSnap.id,
        }));
        setVehicles(loadedVehicles);
      } catch (error) {
        console.error('Error loading vehicles for sign-on:', error);
      }
    };

    loadVehicles();

    return () => {
      cancelled = true;
    };
  }, [mode, tenantId]);

  useEffect(() => {
    if (mode !== 'signOn') return;

    const loadCrewDirectory = async () => {
      try {
        const usersSnap = await getDocs(query(usersCollectionRef(db, tenantId), where('tenantId', '==', tenantId)));
        const rows = usersSnap.docs
          .map((entry) => ({ uid: entry.id, ...(entry.data() as any) }))
          .filter((entry) => entry.uid !== user?.uid)
          .filter((entry) => entry.status === 'Active')
          .map((entry) => ({
            uid: entry.uid,
            name: String(entry.name || 'Unknown'),
            role: (entry.role || Role.Pending) as Role,
            employeeId: entry.employeeId ? String(entry.employeeId) : undefined,
          }));
        setAvailableCrew(rows);
      } catch (error) {
        console.error('Error loading crew directory for sign-on:', error);
      }
    };

    loadCrewDirectory();
  }, [mode, tenantId, user?.uid]);

  useEffect(() => {
    if (mode !== 'signOn' || !user || (!vehicleId && !ghostModeEnabled)) {
      setReadinessInput(null);
      setReadinessMessage('');
      return;
    }

    let cancelled = false;
    const computeReadiness = async () => {
      setReadinessLoading(true);

      try {
        const now = new Date();
        const shiftsSnap = await getDocs(
          query(collection(db, 'shifts'), where('tenantId', '==', tenantId))
        );

        if (cancelled) return;

        const activeShiftRecord = shiftsSnap.docs
          .map((docSnap) => {
            const data = docSnap.data() as any;
            const startDate = data.start?.toDate ? data.start.toDate() : new Date(data.start);
            const endDate = data.end?.toDate ? data.end.toDate() : new Date(data.end);
            return {
              id: docSnap.id,
              ...data,
              start: startDate,
              end: endDate,
            } as Shift;
          })
          .find((shift) => {
            if (shift.status === 'Cancelled') return false;
            const userAssigned = (shift.slots || []).some((slot) => slot.userId === user.uid);
            return userAssigned && shift.start <= now && shift.end >= now;
          });

        if (activeShiftRecord) {
          setShiftStartTime((prev) => prev || activeShiftRecord.start.toISOString());
          setShiftEndTime((prev) => prev || activeShiftRecord.end.toISOString());
        }

        const selectedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId);
        const vehicleReady = ghostModeEnabled
          ? true
          : (selectedVehicle?.status === 'Operational' && isAssetCheckCurrent(selectedVehicle?.lastCheck));

        let kitReady = ghostModeEnabled;
        if (!ghostModeEnabled && activeShiftRecord?.resources?.length) {
          const kitResourceIds = activeShiftRecord.resources
            .filter((resource) => resource.type === 'Kit')
            .map((resource) => resource.id);

          if (kitResourceIds.length > 0) {
            const kitDocs = await Promise.all(
              kitResourceIds.map((kitId) => getDoc(doc(db, 'medical_kits', kitId)))
            );

            if (!cancelled) {
                kitReady = kitDocs.every((kitDoc) => {
                if (!kitDoc.exists()) return false;
                const kit = kitDoc.data() as MedicalKit;
                return kit.status === 'Ready' && isAssetCheckCurrent(kit.lastCheck);
              });
            }
          }
          } else {
            kitReady = true;
        }

        const nextReadiness: ShiftReadinessInput = {
          hasRotaAssignment: Boolean(activeShiftRecord),
          vehicleReady: Boolean(vehicleReady),
          kitReady,
          deviceReady: isDeviceRegistered && !isCheckingDevice,
          override: overrideEnabled
            ? {
                enabled: true,
                reason: overrideReason.trim(),
                approvedByName: overrideApprover.trim(),
              }
            : undefined,
        };

        setReadinessInput(nextReadiness);

        const result = evaluateShiftStartReadiness(nextReadiness);
        if (result.ready && !result.overridden) {
          setReadinessMessage(ghostModeEnabled
            ? 'Ghost resource mode enabled. Dispatch unit will be auto-created without a vehicle assignment.'
            : 'Readiness gate passed. Shift sign-on can proceed.');
        } else if (result.ready && result.overridden) {
          setReadinessMessage('Supervisor override enabled. Sign-on will proceed with audited blockers.');
        } else {
          setReadinessMessage(result.blockers.join(' '));
        }
      } catch (error) {
        console.error('Error computing shift readiness:', error);
        setReadinessInput(null);
        setReadinessMessage('Unable to compute readiness. Please retry.');
      } finally {
        if (!cancelled) {
          setReadinessLoading(false);
        }
      }
    };

    computeReadiness();

    return () => {
      cancelled = true;
    };
  }, [
    isCheckingDevice,
    isDeviceRegistered,
    mode,
    overrideApprover,
    overrideEnabled,
    overrideReason,
    tenantId,
    user,
    vehicleId,
    vehicles,
    ghostModeEnabled,
  ]);

  const checkActiveShift = async () => {
    if (!user) return;
    try {
      const shift = await getActiveCrewShift(tenantId, user.uid);
      if (shift) {
        setActiveShift(shift);
        setMode('active');
      } else {
        setMode('selector');
      }
    } catch (error) {
      console.error('Error checking active shift:', error);
    }
  };

  const handleSignOn = async () => {
    if (!user) {
      setPinError('No active staff login. Please sign in with your own account before shift sign-on.');
      return;
    }

    if (!ghostModeEnabled && !vehicleId) {
      setPinError('Please select a vehicle or enable ghost resource mode.');
      return;
    }

    const resolvedGhostCallSign = ghostCallSign.trim() || buildGhostCallSign(user.name, user.uid);
    if (ghostModeEnabled && !resolvedGhostCallSign) {
      setPinError('Ghost callsign is required in no-vehicle mode.');
      return;
    }

    if (!readinessInput) {
      setPinError('Readiness checks are still loading. Please wait.');
      return;
    }

    if (overrideEnabled && (!overrideApprover.trim() || !overrideReason.trim())) {
      setPinError('Supervisor override requires approver name and reason.');
      return;
    }

    setLoading(true);
    try {
      const resolvedVehicleId = ghostModeEnabled ? `ghost-${user.uid}` : vehicleId;
      const resolvedVehicleCallSign = ghostModeEnabled ? resolvedGhostCallSign : vehicleCallSign;

      const signOnMetadata: CrewSignOnMetadata = {
        additionalCrewMembers: selectedAdditionalCrew.map((member) => ({
          userId: member.uid,
          name: member.name,
          role: member.role,
          employeeId: member.employeeId,
        })),
        plannedShiftStart: shiftStartTime || undefined,
        plannedShiftEnd: shiftEndTime || undefined,
        odometerStart: odometerStart.trim() ? Number(odometerStart) : undefined,
        vdiCompleted,
        vdiNotes: vdiNotes.trim() || undefined,
      };

      const shift = await crewSignOn(
        tenantId,
        user.uid,
        user.name,
        user.role as Role,
        resolvedVehicleId,
        resolvedVehicleCallSign,
        readinessInput,
        signOnMetadata
      );

      await ensureCrewDispatchUnit({
        tenantId,
        callSign: resolvedVehicleCallSign,
        crewUserId: user.uid,
        serviceLines: tenant.serviceLines?.length ? tenant.serviceLines : ['URGENT_RESPONSE'],
        fleetVehicleId: ghostModeEnabled ? undefined : vehicleId,
        isGhostUnit: ghostModeEnabled,
        actorName: user.name,
        actorRole: user.role,
      });

      setActiveShift(shift);
      setMode('active');
      setGhostModeEnabled(false);

      // Check weekly compliance
      const compliance = await checkWeeklyComplianceHours(tenantId, user.uid);
      setWeeklyHours(compliance.totalHours);

      onSignOnComplete?.(shift.id);
    } catch (error: any) {
      setPinError(error.message || 'Sign-on failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakToggle = async () => {
    if (!user || !activeShift) return;

    setBreakLoading(true);
    try {
      const hasActiveBreak = activeShift.breaks?.some((breakRecord: any) => !breakRecord.endTime);

      if (hasActiveBreak) {
        await endBreak(tenantId, user.uid);
      } else {
        await startBreak(tenantId, user.uid, 'Rest');
      }

      const refreshedShift = await getActiveCrewShift(tenantId, user.uid);
      if (refreshedShift) {
        setActiveShift(refreshedShift);
      }
    } catch (error: any) {
      setPinError(error.message || 'Unable to update break status');
    } finally {
      setBreakLoading(false);
    }
  };

  const handleSignOff = async () => {
    if (!user) return;

    const reconciliationInput: ShiftSignOffReconciliationInput = {
      controlledDrugCheckCompleted,
      assetCheckCompleted,
      discrepancies: signOffDiscrepancies,
      notes: signOffReason.trim() || undefined,
      override: signOffOverrideEnabled
        ? {
            enabled: true,
            approvedByName: signOffOverrideApprover.trim(),
            reason: signOffOverrideReason.trim(),
          }
        : undefined,
    };

    const reconciliationResult = evaluateShiftSignOffReconciliation(reconciliationInput);
    if (!reconciliationResult.ready) {
      setPinError(reconciliationResult.blockers.join(' '));
      return;
    }

    setLoading(true);
    try {
      await crewSignOff(tenantId, user.uid, signOffReason.trim() || undefined, reconciliationInput, user.name);
      setActiveShift(null);
      setMode('selector');
      onSignOffComplete?.();
    } catch (error: any) {
      setPinError(error.message || 'Sign-off failed');
    } finally {
      setLoading(false);
    }
  };

  const isAssetCheckCurrent = (lastCheck?: string): boolean => {
    if (!lastCheck) return false;
    const parsed = new Date(lastCheck);
    if (Number.isNaN(parsed.getTime())) return false;
    const now = Date.now();
    const maxAgeMs = 24 * 60 * 60 * 1000;
    return now - parsed.getTime() <= maxAgeMs;
  };

  const filteredCrewOptions = availableCrew.filter((member) => {
    if (selectedAdditionalCrew.some((selected) => selected.uid === member.uid)) return false;
    const search = crewSearch.trim().toLowerCase();
    if (!search) return true;
    return member.name.toLowerCase().includes(search) || String(member.employeeId || '').toLowerCase().includes(search);
  });

  const addCrewMember = (member: { uid: string; name: string; role: Role; employeeId?: string }) => {
    if (selectedAdditionalCrew.some((selected) => selected.uid === member.uid)) return;
    setSelectedAdditionalCrew((prev) => [...prev, member]);
    setCrewSearch('');
    setEmployeeIdSearch('');
    setPinError('');
  };

  const addCrewMemberByEmployeeId = () => {
    const normalized = employeeIdSearch.trim().toLowerCase();
    if (!normalized) return;
    const found = availableCrew.find((member) => String(member.employeeId || '').trim().toLowerCase() === normalized);
    if (!found) {
      setPinError('No active staff member found for that employee ID.');
      return;
    }
    addCrewMember(found);
  };

  const removeCrewMember = (uid: string) => {
    setSelectedAdditionalCrew((prev) => prev.filter((member) => member.uid !== uid));
  };

  const addSignOffDiscrepancy = () => {
    if (!newDiscrepancyDescription.trim()) {
      setPinError('Discrepancy description is required.');
      return;
    }

    setSignOffDiscrepancies((previous) => [
      ...previous,
      {
        id: `disc-${Date.now()}`,
        type: newDiscrepancyType,
        severity: newDiscrepancySeverity,
        description: newDiscrepancyDescription.trim(),
        referenceId: newDiscrepancyReference.trim() || undefined,
        resolved: false,
      },
    ]);

    setNewDiscrepancyDescription('');
    setNewDiscrepancyReference('');
    setPinError('');
  };

  const removeSignOffDiscrepancy = (id: string) => {
    setSignOffDiscrepancies((previous) => previous.filter((entry) => entry.id !== id));
  };

  const toggleDiscrepancyResolved = (id: string, resolved: boolean) => {
    setSignOffDiscrepancies((previous) =>
      previous.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              resolved,
            }
          : entry
      )
    );
  };

  // Sign-on UI
  if (mode === 'signOn') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl p-4 md:p-5 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <LogIn className="w-7 h-7 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800">Sign On To Shift</h2>
            </div>
            <button
              onClick={() => setMode('selector')}
              className="px-3 py-2 text-sm font-semibold border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>

          <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Shared ambulance tablet</p>
            <p className="mt-1 text-sm text-blue-900">
              This device is registered hardware only. The crew member starting the shift must sign in with their own staff account before sign-on.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
            <p className="text-xs uppercase tracking-wide text-slate-500">Signed-in staff member</p>
            <p className="text-lg font-semibold text-slate-800">{user?.name}</p>
            <p className="text-sm text-slate-500">{user?.role}</p>
          </div>

          {/* Vehicle Selection */}
          <div className="mb-5">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-3">
              <input
                type="checkbox"
                checked={ghostModeEnabled}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  setGhostModeEnabled(enabled);
                  if (enabled) {
                    setVehicleId('');
                    setVehicleCallSign('');
                    setGhostCallSign((previous) => previous || buildGhostCallSign(user?.name, user?.uid));
                  }
                }}
              />
              No allocated vehicle (ghost resource mode)
            </label>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              {ghostModeEnabled ? 'Assigned ghost callsign' : 'Select vehicle'}
            </label>
            {ghostModeEnabled ? (
              <input
                type="text"
                value={ghostCallSign}
                onChange={(event) => setGhostCallSign(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="GHOST-UNIT-0001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <select
                value={vehicleId}
                onChange={(e) => {
                  const vehicle = vehicles.find((v) => v.id === e.target.value);
                  if (vehicle) {
                    setVehicleId(vehicle.id);
                    setVehicleCallSign(vehicle.callSign);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select --</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.callSign}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Additional crew */}
          <div className="mb-5 border border-slate-200 rounded-lg p-4 bg-white">
            <p className="text-sm font-semibold text-slate-800 mb-3">Additional crew members</p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-2">
              <input
                type="text"
                value={crewSearch}
                onChange={(event) => setCrewSearch(event.target.value)}
                placeholder="Search by name or employee ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={employeeIdSearch}
                  onChange={(event) => setEmployeeIdSearch(event.target.value)}
                  placeholder="Employee ID"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={addCrewMemberByEmployeeId}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Add ID
                </button>
              </div>
            </div>

            {filteredCrewOptions.length > 0 && (
              <div className="max-h-28 overflow-y-auto border border-slate-200 rounded-lg mb-2">
                {filteredCrewOptions.slice(0, 8).map((member) => (
                  <button
                    key={member.uid}
                    type="button"
                    onClick={() => addCrewMember(member)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                  >
                    <span className="font-semibold text-slate-800">{member.name}</span>
                    <span className="text-slate-500"> · {member.role}{member.employeeId ? ` · ${member.employeeId}` : ''}</span>
                  </button>
                ))}
              </div>
            )}

            {selectedAdditionalCrew.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedAdditionalCrew.map((member) => (
                  <span key={member.uid} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs text-blue-700">
                    {member.name}
                    <button type="button" onClick={() => removeCrewMember(member.uid)} className="text-blue-600 hover:text-blue-800">×</button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">No additional crew selected.</p>
            )}
          </div>

          {/* Shift context fields */}
          <div className="mb-5 border border-slate-200 rounded-lg p-4 bg-white">
            <p className="text-sm font-semibold text-slate-800 mb-3">Shift context</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Planned Shift Start</label>
                <input
                  type="datetime-local"
                  value={shiftStartTime ? shiftStartTime.slice(0, 16) : ''}
                  onChange={(event) => setShiftStartTime(event.target.value ? new Date(event.target.value).toISOString() : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Planned Shift End</label>
                <input
                  type="datetime-local"
                  value={shiftEndTime ? shiftEndTime.slice(0, 16) : ''}
                  onChange={(event) => setShiftEndTime(event.target.value ? new Date(event.target.value).toISOString() : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Current Mileage / Odometer</label>
                <input
                  type="number"
                  value={odometerStart}
                  onChange={(event) => setOdometerStart(event.target.value)}
                  placeholder="e.g. 48217"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 mt-6 text-sm text-slate-700 font-medium">
                  <input
                    type="checkbox"
                    checked={vdiCompleted}
                    onChange={(event) => setVdiCompleted(event.target.checked)}
                  />
                  VDI / Vehicle check completed
                </label>
              </div>
            </div>
            <textarea
              value={vdiNotes}
              onChange={(event) => setVdiNotes(event.target.value)}
              rows={2}
              placeholder="VDI notes or defects (optional)"
              className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Readiness Gate */}
          <div className="mb-5 border border-amber-200 bg-amber-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-amber-900 mb-2">Shift Readiness Gate</p>
            {readinessLoading ? (
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                Checking rota, vehicle, kit, and device readiness...
              </p>
            ) : readinessInput ? (
              <div className="space-y-1 text-sm">
                <p className={`flex items-center gap-1.5 ${readinessInput.hasRotaAssignment ? 'text-green-700' : 'text-red-700'}`}>
                  {readinessInput.hasRotaAssignment ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />} Active rota assignment
                </p>
                <p className={`flex items-center gap-1.5 ${readinessInput.vehicleReady ? 'text-green-700' : 'text-red-700'}`}>
                  {readinessInput.vehicleReady ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />} {ghostModeEnabled ? 'Ghost unit policy check' : 'Vehicle status and current VDI check'}
                </p>
                <p className={`flex items-center gap-1.5 ${readinessInput.kitReady ? 'text-green-700' : 'text-red-700'}`}>
                  {readinessInput.kitReady ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />} {ghostModeEnabled ? 'Kit requirement bypassed for ghost mode' : 'Medical kit status and current check'}
                </p>
                <p className={`flex items-center gap-1.5 ${readinessInput.deviceReady ? 'text-green-700' : 'text-red-700'}`}>
                  {readinessInput.deviceReady ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />} Registered shared device
                </p>
                {readinessMessage && (
                  <p className="pt-2 text-xs text-amber-900">{readinessMessage}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-amber-700">Select a vehicle to run readiness checks.</p>
            )}

            <div className="mt-3 border-t border-amber-200 pt-3">
              <label className="inline-flex items-center gap-2 text-sm text-amber-900 font-medium">
                <input
                  type="checkbox"
                  checked={overrideEnabled}
                  onChange={(event) => setOverrideEnabled(event.target.checked)}
                />
                Supervisor override
              </label>
              {overrideEnabled && (
                <div className="mt-3 space-y-2">
                  <input
                    type="text"
                    value={overrideApprover}
                    onChange={(event) => setOverrideApprover(event.target.value)}
                    placeholder="Approver name"
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm"
                  />
                  <textarea
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                    placeholder="Operational reason for override"
                    rows={2}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {pinError && (
            <div className="flex items-center text-red-600 text-sm mb-4">
              <AlertCircle className="w-4 h-4 mr-2" />
              {pinError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setMode('selector')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSignOn}
              disabled={
                loading ||
                readinessLoading ||
                (!ghostModeEnabled && !vehicleId) ||
                (ghostModeEnabled && !ghostCallSign.trim()) ||
                (!overrideEnabled && readinessInput ? !evaluateShiftStartReadiness(readinessInput).ready : false)
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Signing On...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign On
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active Shift Display
  if (mode === 'active' && activeShift) {
    const shiftStart = new Date(activeShift.shiftStart);
    const now = new Date();
    const shiftDurationMinutes = Math.round(
      (now.getTime() - shiftStart.getTime()) / 1000 / 60
    );
    const shiftHours = Math.floor(shiftDurationMinutes / 60);
    const shiftMinutes = shiftDurationMinutes % 60;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">Active Shift</h2>
          </div>

          {/* Shift Status */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wider">
                  Crew Member
                </p>
                <p className="text-lg font-semibold text-gray-800">
                  {activeShift.crewMemberName}
                </p>
                <p className="text-sm text-gray-600">{activeShift.crewMemberRole}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wider">Vehicle</p>
                <p className="text-lg font-semibold text-gray-800">
                  {activeShift.vehicleId?.startsWith('ghost-') ? `${activeShift.vehicleCallSign} (Ghost)` : activeShift.vehicleCallSign}
                </p>
                <p className="text-sm text-green-600">● Online</p>
              </div>
            </div>

            {Array.isArray(activeShift.additionalCrewMembers) && activeShift.additionalCrewMembers.length > 0 && (
              <div className="mb-3 rounded-lg border border-green-200 bg-white p-3">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Additional Crew</p>
                <p className="text-sm text-gray-800">
                  {activeShift.additionalCrewMembers.map((member: any) => member.name).join(', ')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="rounded-lg border border-green-200 bg-white p-2.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Planned Start</p>
                <p className="text-xs font-semibold text-gray-800">
                  {activeShift.plannedShiftStart ? new Date(activeShift.plannedShiftStart).toLocaleString() : 'Not set'}
                </p>
              </div>
              <div className="rounded-lg border border-green-200 bg-white p-2.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Planned End</p>
                <p className="text-xs font-semibold text-gray-800">
                  {activeShift.plannedShiftEnd ? new Date(activeShift.plannedShiftEnd).toLocaleString() : 'Not set'}
                </p>
              </div>
              <div className="rounded-lg border border-green-200 bg-white p-2.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Odometer Start</p>
                <p className="text-xs font-semibold text-gray-800">{activeShift.odometerStart ?? 'Not set'}</p>
              </div>
              <div className="rounded-lg border border-green-200 bg-white p-2.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">VDI</p>
                <p className="text-xs font-semibold text-gray-800">{activeShift.vdiCompleted ? 'Completed' : 'Pending'}</p>
              </div>
            </div>

            {activeShift.vdiNotes && (
              <div className="mb-3 rounded-lg border border-green-200 bg-white p-2.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">VDI Notes</p>
                <p className="text-xs text-gray-700">{activeShift.vdiNotes}</p>
              </div>
            )}

            <div className="border-t border-green-200 pt-4">
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">
                Shift Duration
              </p>
              <p className="text-2xl font-bold text-green-600">
                {shiftHours}h {shiftMinutes}m
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Started: {shiftStart.toLocaleTimeString()}
              </p>
            </div>

            {weeklyHours !== null && (
              <div className="border-t border-green-200 pt-4 mt-4">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">
                  This Week
                </p>
                <p className="text-lg font-semibold text-gray-800">
                  {weeklyHours}h worked
                </p>
                <p className={`text-xs mt-1 ${weeklyHours > 48 ? 'text-red-600' : 'text-green-600'}`}>
                  {weeklyHours > 48 ? 'Exceeds 48h limit' : 'Within 48h limit'}
                </p>
              </div>
            )}
          </div>

          {/* Breaks Info */}
          {activeShift.breaks.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-gray-800 mb-2">Breaks Taken</p>
              <div className="space-y-1">
                {activeShift.breaks.map((brk: any, i: number) => (
                  <div key={i} className="flex justify-between text-sm text-gray-700">
                    <span>
                      {brk.type} {brk.durationMinutes ? `(${brk.durationMinutes}m)` : '(In progress)'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 gap-3 mb-6">
            <button className="px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition">
              View Dispatch Queue
            </button>
            <button
              onClick={handleBreakToggle}
              disabled={breakLoading}
              className="px-4 py-3 bg-orange-100 text-orange-700 rounded-lg font-medium hover:bg-orange-200 disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {breakLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : activeShift.breaks?.some((breakRecord: any) => !breakRecord.endTime) ? (
                <Square className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {activeShift.breaks?.some((breakRecord: any) => !breakRecord.endTime) ? 'End Break' : 'Start Break'}
            </button>
            <button
              onClick={logout}
              className="px-4 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign Out Account
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-amber-900 mb-3">End-of-Shift Reconciliation</p>

            <div className="space-y-2 mb-4">
              <label className="flex items-center gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={controlledDrugCheckCompleted}
                  onChange={(event) => setControlledDrugCheckCompleted(event.target.checked)}
                />
                Controlled drug count check completed
              </label>
              <label className="flex items-center gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={assetCheckCompleted}
                  onChange={(event) => setAssetCheckCompleted(event.target.checked)}
                />
                Vehicle and kit discrepancy check completed
              </label>
            </div>

            <div className="border-t border-amber-200 pt-3 mb-3">
              <p className="text-xs font-semibold text-amber-900 mb-2">Discrepancy Capture</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  value={newDiscrepancyType}
                  onChange={(event) => setNewDiscrepancyType(event.target.value as 'ControlledDrug' | 'Asset')}
                  className="px-2 py-2 border border-amber-300 rounded text-sm"
                >
                  <option value="ControlledDrug">Controlled Drug</option>
                  <option value="Asset">Asset</option>
                </select>
                <select
                  value={newDiscrepancySeverity}
                  onChange={(event) => setNewDiscrepancySeverity(event.target.value as 'Low' | 'Medium' | 'High')}
                  className="px-2 py-2 border border-amber-300 rounded text-sm"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
              <input
                type="text"
                value={newDiscrepancyDescription}
                onChange={(event) => setNewDiscrepancyDescription(event.target.value)}
                placeholder="Describe discrepancy"
                className="w-full px-2 py-2 border border-amber-300 rounded text-sm mb-2"
              />
              <input
                type="text"
                value={newDiscrepancyReference}
                onChange={(event) => setNewDiscrepancyReference(event.target.value)}
                placeholder="Reference ID (optional)"
                className="w-full px-2 py-2 border border-amber-300 rounded text-sm mb-2"
              />
              <button
                onClick={addSignOffDiscrepancy}
                className="px-3 py-2 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-700 transition"
              >
                Add Discrepancy
              </button>
            </div>

            {signOffDiscrepancies.length > 0 && (
              <div className="space-y-2 mb-3">
                {signOffDiscrepancies.map((entry) => (
                  <div key={entry.id} className="bg-white border border-amber-200 rounded p-2 text-xs">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-semibold text-slate-700">{entry.type} · {entry.severity}</p>
                        <p className="text-slate-700">{entry.description}</p>
                        {entry.referenceId && <p className="text-slate-500">Ref: {entry.referenceId}</p>}
                      </div>
                      <button
                        onClick={() => removeSignOffDiscrepancy(entry.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                    <label className="flex items-center gap-2 mt-2 text-slate-700">
                      <input
                        type="checkbox"
                        checked={entry.resolved === true}
                        onChange={(event) => toggleDiscrepancyResolved(entry.id, event.target.checked)}
                      />
                      Mark as resolved
                    </label>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-amber-200 pt-3">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-amber-900">
                <input
                  type="checkbox"
                  checked={signOffOverrideEnabled}
                  onChange={(event) => setSignOffOverrideEnabled(event.target.checked)}
                />
                Supervisor override for unresolved discrepancies
              </label>
              {signOffOverrideEnabled && (
                <div className="space-y-2 mt-2">
                  <input
                    type="text"
                    value={signOffOverrideApprover}
                    onChange={(event) => setSignOffOverrideApprover(event.target.value)}
                    placeholder="Approver name"
                    className="w-full px-2 py-2 border border-amber-300 rounded text-sm"
                  />
                  <textarea
                    value={signOffOverrideReason}
                    onChange={(event) => setSignOffOverrideReason(event.target.value)}
                    rows={2}
                    placeholder="Reason for override"
                    className="w-full px-2 py-2 border border-amber-300 rounded text-sm"
                  />
                </div>
              )}
            </div>

            <textarea
              value={signOffReason}
              onChange={(event) => setSignOffReason(event.target.value)}
              rows={2}
              placeholder="Sign-off notes (optional)"
              className="w-full px-2 py-2 border border-amber-300 rounded text-sm mt-3"
            />
          </div>

          {pinError && (
            <div className="flex items-center text-red-600 text-sm mb-4">
              <AlertCircle className="w-4 h-4 mr-2" />
              {pinError}
            </div>
          )}

          {/* Sign Off Button */}
          <button
            onClick={handleSignOff}
            disabled={loading || !controlledDrugCheckCompleted || !assetCheckCompleted}
            className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Signing Off...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4" />
                Sign Off
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Mode Selector
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full mx-4">
        <div className="flex items-center justify-center mb-6">
          <Lock className="w-8 h-8 text-blue-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-800">Crew Management</h2>
        </div>

        <p className="text-gray-600 text-center mb-8">
          Hello, {user?.name}. What would you like to do?
        </p>

        <div className="space-y-3">
          <button
            onClick={() => setMode('signOn')}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-between"
          >
            <span>Sign On to Shift</span>
            <LogIn className="w-5 h-5" />
          </button>

          <button
            onClick={() => {
              /* Show historical shifts */
            }}
            className="w-full px-6 py-4 bg-gray-100 text-gray-800 rounded-lg font-semibold hover:bg-gray-200 transition"
          >
            View Shift History
          </button>

          <button
            onClick={() => {
              /* Show compliance info */
            }}
            className="w-full px-6 py-4 bg-gray-100 text-gray-800 rounded-lg font-semibold hover:bg-gray-200 transition"
          >
            Compliance Status
          </button>

          <button
            onClick={logout}
            className="w-full px-6 py-4 bg-red-50 text-red-700 rounded-lg font-semibold hover:bg-red-100 transition flex items-center justify-between"
          >
            <span>Sign Out Account</span>
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-8">
          Version 1.0 — Crew Management System
        </p>
      </div>
    </div>
  );
};

export default CrewSignOnComponent;
